import React, { useState, useEffect, ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { Dispatch } from 'redux';
import { actions as projectActions } from '../../actions/projectActions';
import { actions as projectLocationActions } from '../../actions/projectLocationActions';
import { actions as projectMgrDashboardActions } from '../../actions/projectMgrDashboardActions';
import { actions as newProjectWizardActions } from '../../actions/newProjectWizardActions';
import * as types from '../../constants/actionTypes';
import Routes from '../../constants/routes';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import ProjectDetails from './ContentWidgets/ProjectDetails';
import ProjectLocations from './ContentWidgets/ProjectLocations';
import AddReviewTasks from './ContentWidgets/AddReviewTasks';
import './NewProjectWizardPage.scss';
import NewProjectWizardStepNumber from './NewProjectWizardStepNumber';
import Modal from '../Common/Modal/Modal';
import Notification from '../../components/Common/Notification';
import isEqual from 'lodash.isequal';
import { Project, NewProjectData, LocationOption } from '../../types/common';
import LinkTasks from './ContentWidgets/LinkTasks';
import CopyTasks from './ContentWidgets/CopyTasks';
import ImportTasks from './ContentWidgets/ImportTasks/ImportTasks';
import ReviewImportedTasks from './ContentWidgets/ReviewImportedTasks';
import Publish from './ContentWidgets/Publish/Publish';

interface NewProjectWizardPageProps {
	location: any;
	subscriptionId: number;
}

interface StepValidation {
	stepOne: boolean;
	stepTwo: boolean;
	stepThree: boolean;
	stepFour: boolean;
}

const NewProjectWizardPage = ({ location, subscriptionId }: NewProjectWizardPageProps): ReactElement => {
	const [wizardStep, setWizardStep] = useState<number>(1); // 1, 2, 3, 4
	const [wizardSubStep, setWizardSubStep] = useState<string>(''); // a.1, b.2, etc.
	const [targetWizardStep, setTargetWizardStep] = useState<number>(0);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isDirty, setIsDirty] = useState<boolean>(true);
	const [showExitSaveConfirm, setShowExitSaveConfirm] = useState<boolean>(false);
	const [showStepNavSaveConfirm, setShowStepNavSaveConfirm] = useState<boolean>(false);
	const [isReady, setIsReady] = useState<boolean>(false);
	const [project, setProject] = useState<NewProjectData>({
		id: 0,
		name: '',
		description: '',
		abbreviation: '',
		projectManagerId: 0,
		supportAgentId: useSelector((state) => state.user.id),
		isActive: true,
		projectKey: '',
		locationIds: [],
		baselineIds: [],
	});
	const [projectCopy, setProjectCopy] = useState<NewProjectData>({
		...project,
	}); // Copy of project initial values for possible reverts
	const [stepValidation, setStepValidation] = useState<StepValidation>({
		stepOne: true,
		stepTwo: true,
		stepThree: true,
		stepFour: true,
	});

	// Keep this state in parent so that location data doesn't disappear when re-rendered
	const [availLocations, setAvailLocations] = useState<Array<LocationOption>>([]);
	const [selectedLocations, setSelectedLocations] = useState<Array<LocationOption>>([]);
	const [isNextDisabled, setIsNextDisabled] = useState<boolean>(false);
	const [importType, setImportType] = useState<string>('');
	const [goToPch, setGoToPch] = useState<boolean>(false);

	const history = useHistory();

	const dispatch: Dispatch = useDispatch();
	const saveProject = async (project: Project) => dispatch(projectActions.saveProject(project));
	const addLocationsToScope = async (request) => dispatch(projectLocationActions.addToScope(request));
	const removeLocationsFromScope = async (request) => dispatch(projectLocationActions.removeFromScope(request));
	const linkProjectBaselines = async (request) => dispatch(projectMgrDashboardActions.linkProjectBaseline(request));
	const copyProjectBaselines = async (request) => dispatch(newProjectWizardActions.copyProjectBaselines(request));

	useEffect(() => {
		document.title = 'New Project';
	}, []);

	const renderWizardStep = () => {
		// Update the wizard content based upon the wizardStep that is set
		switch (wizardStep) {
			case 1:
				return (
					<ProjectDetails
						projectData={project}
						setProjectData={updateProjectData}
						setIsLoading={setIsLoading}
						isFormValid={stepValidation.stepOne}
					/>
				);
			case 2:
				return (
					<ProjectLocations
						projectData={project}
						setProjectData={updateProjectData}
						setIsLoading={setIsLoading}
						subscriptionId={subscriptionId}
						locationsState={{
							availableLocations: availLocations,
							selectedLocations: selectedLocations,
							setAvailableLocations: setAvailLocations,
							setSelectedLocations: setSelectedLocations,
						}}
					/>
				);
			case 3:
				switch (wizardSubStep) {
					case 'a.1':
						return (
							<LinkTasks
								setProjectData={updateProjectData}
								setIsLoading={setIsLoading}
								chooseDiffAddTaskMethod={chooseDiffAddTaskMethod}
							/>
						);
					case 'b.1':
						return (
							<CopyTasks
								setProjectData={updateProjectData}
								setIsLoading={setIsLoading}
								chooseDiffAddTaskMethod={chooseDiffAddTaskMethod}
							/>
						);
					case 'c.1':
						return (
							<ImportTasks
								setProjectData={updateProjectData}
								projectKey={project.projectKey}
								setIsLoading={setIsLoading}
								chooseDiffAddTaskMethod={chooseDiffAddTaskMethod}
								setWizardSubStep={setWizardSubStepState}
								importType={importType}
							/>
						);
					case 'c.2':
						return (
							<div>
								{isReady ? (
									<ReviewImportedTasks setIsLoading={setIsLoading} baselineId={project.baselineIds[0]} />
								) : (
									<p>Loading...</p>
								)}
							</div>
						);
					default:
						return (
							<AddReviewTasks
								setWizardSubStep={setWizardSubStepState}
								setImportType={setImportType}
								projectKey={project.projectKey}
								subscriptionId={subscriptionId}
								nextStep={next}
							/>
						);
				}
			case 4:
				return <Publish projectData={project} setIsLoading={setIsLoading} isLoading={isLoading} />;
			default:
				return (
					<ProjectDetails
						projectData={project}
						setProjectData={updateProjectData}
						setIsLoading={setIsLoading}
						isFormValid={stepValidation.stepOne}
					/>
				);
		}
	};

	const preExit = (promptForSave: boolean) => {
		if (isDirty) {
			promptForSave
				? setShowExitSaveConfirm(true) // dirty and prompt for save before exiting
				: exit(true); // dirty and exiting with autosave
		} else exit(false); // clean exit
	};

	const exit = async (save: boolean) => {
		setShowExitSaveConfirm(false); // Close a possibly open save modal

		if (save && !(await saveProjectData())) return;

		// "save and close" button & usage of breadcrumb behave the same:
		//      - if any step components are dirty, prompt user for save
		//      - navigate to Location Dashboard (if user came directly here, etc.)

		history.push(Routes.DashboardCorporate);
	};

	const next = async (baselineId: number = 0) => {
		// Only reset isReady if we're not already in the review step
		if (wizardSubStep !== 'c.2') {
			setIsReady(false);
		}

		if (typeof project.baselineIds[0] === 'undefined' && baselineId > 0) {
			project.baselineIds[0] = baselineId;

			setProject({
				...project,
				baselineIds: [...project.baselineIds],
			});
		}

		if (baselineId > 0) {
			setIsReady(true);
			setWizardSubStep('c.2');
			return;
		}

		if (isDirty && !(await saveProjectData())) return;

		// Only allowed to link, copy or upload baselines once - otherwise get taken immediately to step 4
		if (wizardStep === 2 && projectCopy.baselineIds.length) setWizardStepState(4);
		else if (wizardStep < 4) setWizardStepState(wizardStep + 1);
	};

	const setWizardStepState = (stepNum: number) => {
		// Certain steps disable the next button
		// if (stepNum === 3)
		//     setIsNextDisabled(true);

		setWizardStep(stepNum);
	};

	const setWizardSubStepState = (subStep: string) => {
		// Certain steps disable the next button
		if (subStep === 'c.1') setIsNextDisabled(true);

		// And some steps re-enable it
		if (subStep === 'c.2') {
			setIsNextDisabled(false);
			setIsReady(true); // Set isReady to true when navigating to review imported tasks
		} else {
			// Only reset isReady when leaving the review step
			if (wizardSubStep === 'c.2') {
				setIsReady(false);
			}
		}

		setWizardSubStep(subStep);
	};

	const isMainProjectDataChanged = () => {
		return (
			project.name !== projectCopy.name ||
			project.description !== projectCopy.description ||
			project.abbreviation !== projectCopy.abbreviation ||
			project.projectManagerId !== projectCopy.projectManagerId
		);
	};

	const saveProjectData = async () => {
		if (!validateStepForm()) return false;

		setIsLoading(true);
		let success = false;

		try {
			if (isMainProjectDataChanged()) {
				const request: Project = { ...project };
				const result = await saveProject(request);
				success = result.type === types.CREATE_PROJECT_SUCCESS || result.type === types.UPDATE_PROJECT_SUCCESS;

				if (success) {
					setProject((prevState) => ({
						...prevState,
						locationIds: [...prevState.locationIds],
						id: result.payload.id,
						projectKey: result.payload.project.projectKey,
					}));
				} else if (result.type === types.UPDATE_PROJECT_FAILURE || result.type === types.CREATE_PROJECT_FAILURE) {
					Notification('error', 'An Error Occurred', result.payload.message);
				}
			} else if (!isEqual(project.locationIds, projectCopy.locationIds)) {
				// Find out if we need to add and/or remove location(s) from scope
				const locsToAdd = project.locationIds.filter((locId) => !projectCopy.locationIds.includes(locId));
				const locsToRemove = projectCopy.locationIds.filter((locId) => !project.locationIds.includes(locId));

				let addSuccess = true;
				let removeSuccess = true;

				if (locsToAdd.length) addSuccess = await addToScope(locsToAdd);

				if (addSuccess && locsToRemove.length) removeSuccess = await removeFromScope(locsToRemove);

				success = addSuccess && removeSuccess;
			} else if (!isEqual(project.baselineIds, projectCopy.baselineIds)) {
				// Only add baselines - not in scope to add & remove

				if (wizardStep === 3 && wizardSubStep === 'a.1') success = await linkBaselines();
				else if (wizardStep === 3 && wizardSubStep === 'b.1') success = await copyBaselines();
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}

		if (success) {
			Notification('success', 'Save Successful', 'Project saved successfully');

			// Re-establish the copy
			setProjectCopy({
				...project,
				locationIds: [...project.locationIds],
				baselineIds: [...project.baselineIds],
			});

			setIsDirty(false);
		}

		return success;
	};

	const addToScope = async (locIds) => {
		let success = false;

		const request = {
			subscriptionId: subscriptionId,
			projectKey: project.projectKey,
			locationIds: locIds,
		};
		const response = await addLocationsToScope(request);
		if (response.type === types.ADD_TO_PROJECTLOCATION_SCOPE_SUCCESS) {
			success = true;
		} else if (response.type === types.ADD_TO_PROJECTLOCATION_SCOPE_FAILURE) {
			Notification('error', 'An Error Occurred', response.payload.message);
		}

		return success;
	};

	const removeFromScope = async (locIds) => {
		let success = false;

		const request = {
			subscriptionId: subscriptionId,
			projectKey: project.projectKey,
			locationIds: locIds,
		};
		const response = await removeLocationsFromScope(request);
		if (response.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_SUCCESS) {
			// Ignoring if response says we skipped some locations
			success = true;
		} else if (response.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_FAILURE)
			Notification('error', 'An Error Occurred', response.payload.message);

		return success;
	};

	const linkBaselines = async () => {
		let success = false;

		const request = {
			subscriptionId: subscriptionId,
			projectKey: project.projectKey,
			baselineIds: project.baselineIds,
		};
		const response = await linkProjectBaselines(request);
		if (response.type === types.LINK_PROJECT_BASELINE_SUCCESS) {
			success = true;
		} else if (response.type === types.LINK_PROJECT_BASELINE_FAILURE) {
			Notification('error', 'An Error Occurred', response.payload.message);
		}

		return success;
	};

	const copyBaselines = async () => {
		let success = false;

		const request = {
			subscriptionId: subscriptionId,
			projectKey: project.projectKey,
			baselineIds: project.baselineIds.filter((value, index, array) => array.indexOf(value) === index), // Distinct values
		};
		const response = await copyProjectBaselines(request);
		if (response.type === types.NEW_PROJECT_WIZARD_COPY_PROJECT_BASELINES_SUCCESS) {
			success = true;
		} else if (response.type === types.NEW_PROJECT_WIZARD_COPY_PROJECT_BASELINES_FAILURE) {
			Notification('error', 'An Error Occurred', response.payload.message);
		}

		return success;
	};

	// Called from child components
	const updateProjectData = (func, skipIsDirty = false) => {
		setProject(func); // Pass callback from child component

		if (!skipIsDirty) setIsDirty(true);
		else {
			// Since we are not going to save we should set the copy
			setProjectCopy({
				...project,
				locationIds: [...project.locationIds],
				baselineIds: [...project.baselineIds],
			});
		}
	};

	const jumpToStep = (stepNum: number) => {
		// Only allowed to jump to other steps once step 1 is complete
		if (project.id === 0) return;

		setIsNextDisabled(false);

		// Only allowed to link, copy or upload baselines once - otherwise get taken immediately to step 4
		if (stepNum === 3 && projectCopy.baselineIds.length) stepNum = 4;

		if (isDirty) {
			setTargetWizardStep(stepNum);
			setShowStepNavSaveConfirm(true);
		} else setWizardStepState(stepNum);
	};

	const jumpToStepAfterPrompt = async (save: boolean) => {
		setShowStepNavSaveConfirm(false);

		if (save) {
			if (!(await saveProjectData())) {
				return;
			}
		} else revertStep(wizardStep);

		setWizardStepState(targetWizardStep);
		setIsDirty(false);
	};

	const validateStepForm = () => {
		switch (wizardStep) {
			case 1: {
				if (project.name.trim().length < 1 || project.projectManagerId === 0) {
					setStepValidation((prevState) => ({
						...prevState,
						stepOne: false,
					}));
					return false;
				} else {
					return true;
				}
			}
			case 2:
			case 3:
			case 4:
			default:
				return true;
		}
	};

	const revertStep = (stepNum) => {
		// Revert the data in the specified step number
		switch (stepNum) {
			case 1:
				setProject((prevState) => ({
					...prevState,
					locationIds: [...prevState.locationIds],
					name: projectCopy.name,
					description: projectCopy.description,
					projectManagerId: projectCopy.projectManagerId,
					abbreviation: '',
				}));
				break;
			case 2:
				setProject((prevState) => ({
					...prevState,
					locationIds: [...projectCopy.locationIds],
				}));

				// Move any selected locations that haven't been saved back to available
				setAvailLocations((existing) => [...existing, ...selectedLocations.filter((x) => !projectCopy.locationIds.includes(x.id))]);

				// Move any deselected locations that haven't been saved back to selected
				setSelectedLocations((existing) => [...existing, ...availLocations.filter((x) => projectCopy.locationIds.includes(x.id))]);

				setSelectedLocations((existing) => existing.filter((x) => projectCopy.locationIds.includes(x.id)));
				setAvailLocations((existing) => [...existing.filter((x) => !projectCopy.locationIds.includes(x.id))]);
				break;
			case 3:
				clearBaselineIds();
				break;
			case 4:
				break;
			default:
				return;
		}
	};

	const getWizardStepClass = (stepNum) => {
		if (wizardStep > 1) return 'wizard-step-active';

		if (stepNum <= wizardStep) return 'wizard-step-active';

		return 'wizard-step-inactive';
	};

	const wizardStepNumbersData = [
		{
			stepName: 'Project Details',
			stepDescription: 'Add information about your project',
		},
		{
			stepName: 'Select Teams',
			stepDescription: 'Choose which teams or locations will complete this project',
		},
		{
			stepName: 'Add & Review Tasks',
			stepDescription: 'Add or create tasks for your project',
		},
		{
			stepName: 'Publish Project',
			stepDescription: 'Add Zero Dates and publish to teams and locations',
			isFinalStep: true,
		},
	];

	const renderWizardStepNumbers = () =>
		wizardStepNumbersData.map((stepData, index) => (
			<div className={getWizardStepClass(index + 1)} key={'wizard-step' + (index + 1)} onClick={() => jumpToStep(index + 1)}>
				<NewProjectWizardStepNumber
					stepName={stepData.stepName}
					stepDescription={stepData.stepDescription}
					stepNum={index + 1}
					isComplete={wizardStep > index + 1}
					isActive={wizardStep === index + 1}
					isFinalStep={stepData.isFinalStep}
				/>
			</div>
		));

	const clearBaselineIds = () => {
		setProject((prevState) => ({
			...prevState,
			locationIds: [...prevState.locationIds],
			baselineIds: [],
		}));
	};

	const chooseDiffAddTaskMethod = () => {
		clearBaselineIds();
		setIsReady(false); // Reset isReady when going back to method selection
		setWizardSubStep('');
	};

	const navToPch = async () => {
		// Avoid potential race condition by setting this first
		if (!project.projectKey) {
			setGoToPch(true);	// trigger the useEffect below because we have to wait for the projectKey state to be set
		}

		// Similar to clicking "show advanced" button on last step but here we must save first
		if ((isDirty || wizardStep === 1) && !(await saveProjectData()))
			return;

		// If we have a projectKey we can navigate immediately
		if (project.projectKey)
			history.push(Routes.NewProjectManagerDashboard + `/${project.projectKey}`);
	}

	useEffect(() => {
		if (goToPch && project.projectKey) {
			history.push(Routes.NewProjectManagerDashboard + `/${project.projectKey}`);
		}
	}, [project.projectKey]);

	return (
		<div className='new-project-wizard'>
			<LoadingIndicator isLoading={isLoading} isFullscreen={true}>
				<div>
					<div className='wizard-breadcrumb row g-0'>
						<div className='col-12'>
							<a onClick={() => preExit(true)}>
								<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14' fill='none'>
									<path
										d='M9.6126 13.5C9.50961 13.5005 9.40755 13.4804 9.31243 13.4409C9.21731 13.4014 9.13103 13.3433 9.05865 13.2701L3.83277 8.04416C3.68597 7.89718 3.60352 7.69795 3.60352 7.49022C3.60352 7.28249 3.68597 7.08325 3.83277 6.93628L9.05865 1.71039C9.20725 1.57192 9.40379 1.49654 9.60687 1.50012C9.80995 1.50371 10.0037 1.58597 10.1473 1.72959C10.291 1.87322 10.3732 2.06698 10.3768 2.27005C10.3804 2.47313 10.305 2.66968 10.1665 2.81827L5.4946 7.49022L10.1665 12.1622C10.3133 12.3091 10.3958 12.5084 10.3958 12.7161C10.3958 12.9238 10.3133 13.1231 10.1665 13.2701C10.0942 13.3433 10.0079 13.4014 9.91277 13.4409C9.81764 13.4804 9.71559 13.5005 9.6126 13.5Z'
										fill='#6C757D'
									/>
								</svg>
								<span>Overview</span>
							</a>
						</div>
					</div>

					<div className='heading-nav row g-0'>
						<div className='col'>
							<div className='heading-title'>New Project</div>
						</div>
						<div className='col-auto'>
							{wizardStep !== 4 && (
								<>
									<button
										type='button'
										className='pacer61-btn pacer61-secondary-btn'
										onClick={() => navToPch()}>
										Show Advanced Options
									</button>
									<button type='button' className='pacer61-btn pacer61-secondary-btn' onClick={() => preExit(false)}>
										Save to Drafts and Exit
									</button>
									<button type='button' className='pacer61-btn pacer61-primary-btn' onClick={() => next()} disabled={isNextDisabled}>
										Next
									</button>
								</>
							)}

							{wizardStep === 4 && (
								<>
									<button
										type='button'
										className='pacer61-btn pacer61-secondary-btn'
										onClick={() => history.push(Routes.NewProjectManagerDashboard + `/${project.projectKey}`)}>
										Show Advanced Options
									</button>
									<button
										type='button'
										className='pacer61-btn pacer61-primary-btn'
										onClick={() => history.push(Routes.DashboardCorporate)}>
										Finished! Return to Overview
									</button>
								</>
							)}
						</div>
					</div>

					<div className='wizard-main row g-0'>
						<div className='wizard-steps col-12 col-md-2'>{renderWizardStepNumbers()}</div>

						<div className='wizard-content col'>{renderWizardStep()}</div>
					</div>

					{showExitSaveConfirm && (
						<Modal
							id='confirmExitSaveModal'
							title='Save changes?'
							body={<p>Do you want to save your changes?</p>}
							primaryButtonText='Save'
							secondaryButtonText='Discard'
							onPrimaryClick={() => exit(true)}
							onSecondaryClick={() => exit(false)}
							onCloseClick={() => setShowExitSaveConfirm(false)}
						/>
					)}
					{showStepNavSaveConfirm && (
						<Modal
							id='confirmStepNavSaveModal'
							title='Save changes?'
							body={<p>Do you want to save your changes?</p>}
							primaryButtonText='Save'
							secondaryButtonText='Discard'
							onPrimaryClick={() => jumpToStepAfterPrompt(true)}
							onSecondaryClick={() => jumpToStepAfterPrompt(false)}
							onCloseClick={() => setShowStepNavSaveConfirm(false)}
						/>
					)}
				</div>
			</LoadingIndicator>
		</div>
	);
};

export default NewProjectWizardPage;
